<template>
  <v-layout class="insurance" wrap>
    <v-flex xs12 pb-0>
      <v-layout wrap align-center>
        <v-flex xs12 sm6 md6 lg6 mt-2>
          <h1 class="headline">{{ $t('auto.insuranceInspection') }}</h1>
        </v-flex>
      </v-layout>
    </v-flex>

    <v-flex xs12 mt-5>
      <div class="d-flex search">
        <v-text-field
          v-model="searchString"
          class="insurance__search"
          solo
          hide-details
          :placeholder="$t('auto.fields.numberPlaceholder')"
        />
        <v-btn
          type="button"
          class="search-btn warning"
          depressed
          large
          @click="filters.search = searchString"
        >
          {{ $t('common.btns.search') }}
        </v-btn>
      </div>
    </v-flex>

    <v-flex d-flex>
      <insurance-color-select
        v-model="filters.expiryKaskoColor"
        class="insurance__filter"
        :label="$t('auto.labels.expireDateKasko')"
      />
      <insurance-color-select
        v-model="filters.expiryOsgpoColor"
        class="insurance__filter"
        :label="$t('auto.labels.expireDateOsgpo')"
      />
      <insurance-color-select
        v-model="filters.closeCheckupColor"
        class="insurance__filter"
        :label="$t('auto.labels.expireDateCheckup')"
        only-colors
      />
    </v-flex>

    <v-flex xs12>
      <v-data-table
        class="insurance__list"
        :headers="headers"
        :items="list"
        :loading="loading"
        :items-per-page="perPage"
        hide-default-header
        hide-default-footer
      >
        <template #header="{ props }">
          <thead class="v-data-table-header">
            <tr>
              <template v-for="(header, key) in props.headers">
                <th
                  v-show="!header.hidden"
                  class="text-align-center"
                  :class="{ section: header.sections }"
                  :style="{ minWidth: header.width || 'auto' }"
                  :key="`header-${key}`"
                  :colspan="header.sections ? header.sections.length : 1"
                >
                  <template v-if="header.sections">
                    {{ header.text }}
                  </template>
                </th>
              </template>
            </tr>

            <tr>
              <template v-for="(header, key) in props.headers">
                <template v-if="header.sections">
                  <th
                    v-for="(subheader, idx) in header.sections"
                    :key="`subheader-${key}-${idx}`"
                    class="section"
                    :class="{ last: idx === header.sections.length - 1 }"
                  >
                    {{ subheader.text }}
                  </th>
                </template>

                <th v-show="!header.hidden" :key="key" v-else>
                  {{ header.text }}
                </th>
              </template>
            </tr>
          </thead>
        </template>

        <template #body="{ items }">
          <tbody>
            <tr v-for="(item, key) in items" :key="key">
              <td>{{ item.num }}</td>
              <td>{{ item.id }}</td>
              <td>
                <v-btn
                  color="#464646"
                  :to="`/autos/${item.id}/info`"
                  text
                  small
                >
                  <b>{{ item.number }}</b>
                </v-btn>
              </td>
              <td>{{ item.brand }}</td>
              <td>{{ item.model }}</td>
              <td>{{ item.year }}</td>
              <td>
                <button
                  class="edit-btn"
                  :disabled="!$can('car_edit_insurance') || !item.kasko"
                  @click="
                    () => {
                      editDialog.opened = true;
                      editDialog.param = 'kaskoName';
                      editDialog.label = $t('auto.labels.kaskoName');
                      editDialog.value = item.kaskoName;
                      editDialog.itemId = item.id;
                    }
                  "
                >
                  {{ item.kaskoName || '-' }}
                </button>
              </td>
              <td :class="getExpireStateColor(item.expiryKaskoDays)">
                <!-- <edit-on-click-field
                  :label="item.expiryKasko || $t('common.fields.noItem')"
                  :disabled="!$can('car_edit_insurance')"
                >
                  <menu-datepicker
                    label=""
                    :value="item.expiryKasko"
                    dense
                    hide-details
                    no-max
                    clearable
                    @change="updateKasko(item.id, $event)"
                  />
                </edit-on-click-field> -->
                <div class="d-flex justify-space-between">
                  <button
                    class="edit-btn nowrap"
                    @click="
                      () => {
                        insuranceFilesDialog.opened = true;
                        insuranceFilesDialog.date = item.expiryKasko || '';
                        insuranceFilesDialog.type = 'kasko';
                        insuranceFilesDialog.itemId = item.id;
                        insuranceFilesDialog.files = item.kaskoFiles;
                        insuranceFilesDialog.onlyFiles = false;
                        insuranceFilesDialog.readOnly = false;
                      }
                    "
                  >
                    {{ item.expiryKasko || $t('common.fields.noItem') }}
                  </button>

                  <v-tooltip color="info" top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        v-on="on"
                        v-bind="attrs"
                        class="ml-3"
                        color="info"
                        small
                        icon
                        @click="
                          () => {
                            insuranceFilesDialog.opened = true;
                            insuranceFilesDialog.type = 'kasko';
                            insuranceFilesDialog.itemId = item.id;
                            insuranceFilesDialog.files = item.kaskoFiles;
                            insuranceFilesDialog.onlyFiles = true;
                            insuranceFilesDialog.readOnly = true;
                          }
                        "
                      >
                        <v-icon size="20">mdi-file-download-outline</v-icon>
                      </v-btn>
                    </template>

                    {{ $t('common.docs') }}
                  </v-tooltip>
                </div>
              </td>
              <td>
                <button
                  class="edit-btn"
                  :disabled="!$can('car_edit_insurance') || !item.osgpo"
                  @click="
                    () => {
                      editDialog.opened = true;
                      editDialog.param = 'osgpoName';
                      editDialog.label = $t('auto.labels.osgpoName');
                      editDialog.value = item.osgpoName;
                      editDialog.itemId = item.id;
                    }
                  "
                >
                  {{ item.osgpoName || '-' }}
                </button>
              </td>

              <td :class="getExpireStateColor(item.expiryOsgpoDays)">
                <!-- <edit-on-click-field
                  :label="item.expiryOsgpo || $t('common.fields.noItem')"
                  :disabled="!$can('car_edit_insurance')"
                >
                  <menu-datepicker
                    label=""
                    :value="item.expiryOsgpo"
                    dense
                    hide-details
                    no-max
                    clearable
                    @change="updateOsgpo(item.id, $event)"
                  />
                </edit-on-click-field> -->

                <div class="d-flex justify-space-between">
                  <button
                    class="edit-btn nowrap"
                    @click="
                      () => {
                        insuranceFilesDialog.opened = true;
                        insuranceFilesDialog.date = item.expiryOsgpo || '';
                        insuranceFilesDialog.type = 'osgpo';
                        insuranceFilesDialog.itemId = item.id;
                        insuranceFilesDialog.files = item.osgpoFiles;
                        insuranceFilesDialog.onlyFiles = false;
                        insuranceFilesDialog.readOnly = false;
                      }
                    "
                  >
                    {{ item.expiryOsgpo || $t('common.fields.noItem') }}
                  </button>

                  <v-tooltip color="info" top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        v-on="on"
                        v-bind="attrs"
                        class="ml-3"
                        color="info"
                        small
                        icon
                        @click="
                          () => {
                            insuranceFilesDialog.opened = true;
                            insuranceFilesDialog.type = 'osgpo';
                            insuranceFilesDialog.itemId = item.id;
                            insuranceFilesDialog.files = item.osgpoFiles;
                            insuranceFilesDialog.onlyFiles = true;
                            insuranceFilesDialog.readOnly = true;
                          }
                        "
                      >
                        <v-icon size="20">mdi-file-download-outline</v-icon>
                      </v-btn>
                    </template>

                    {{ $t('common.docs') }}
                  </v-tooltip>
                </div>
              </td>
              <td>
                <edit-on-click-field
                  :label="item.openCheckup"
                  :disabled="!$can('car_edit_insurance')"
                >
                  <menu-datepicker
                    label=""
                    :value="item.openCheckup"
                    dense
                    hide-details
                    no-max
                    @change="updateItem(item.id, { openCheckup: $event })"
                  />
                </edit-on-click-field>
              </td>
              <td :class="getExpireStateColor(item.closeCheckupDays)">
                <edit-on-click-field
                  :label="item.closeCheckup"
                  :disabled="!$can('car_edit_insurance')"
                >
                  <menu-datepicker
                    label=""
                    :value="item.closeCheckup"
                    dense
                    hide-details
                    no-max
                    @change="updateItem(item.id, { closeCheckup: $event })"
                  />
                </edit-on-click-field>
              </td>
            </tr>
          </tbody>
        </template>

        <template #footer>
          <v-divider />
          <div class="pt-1 pb-2">
            <v-pagination
              color="warning"
              :value="currentPage"
              :length="lastPage"
              dark
              circle
              @input="fetchList($event)"
            />
          </div>
        </template>
      </v-data-table>
    </v-flex>

    <text-field-edit-dialog
      v-model="editDialog.opened"
      :param-name="editDialog.param"
      :label="editDialog.label"
      :currentValue="editDialog.value"
      @save="updateItem(editDialog.itemId, $event)"
    />

    <insurance-files-dialog
      v-model="insuranceFilesDialog.opened"
      :auto-id="insuranceFilesDialog.itemId"
      :date="insuranceFilesDialog.date"
      :files="insuranceFilesDialog.files"
      :type="insuranceFilesDialog.type"
      :only-files="insuranceFilesDialog.onlyFiles"
      :read-only="insuranceFilesDialog.readOnly"
      :no-download="!$can('auto_files_download')"
      :no-delete="!$can('auto_files_delete')"
      @save="updateInsurance"
      @delete="onInsuranceFilesDelete"
    />

    <v-snackbar v-model="snack.opened" :color="snack.color" bottom right>
      {{ snack.message }}
    </v-snackbar>
  </v-layout>
</template>

<script>
import { mapState } from 'vuex';
import checkRouteAccess from '@/helpers/checkRouteAccess';
import InsuranceColorSelect from '@/components/auto/InsuranceColorSelect.vue';
import EditOnClickField from '@/components/shared/EditOnClickField.vue';
import MenuDatepicker from '@/components/shared/MenuDatepicker.vue';
import TextFieldEditDialog from '@/components/shared/TextFieldEditDialog.vue';
import InsuranceFilesDialog from '@/components/auto/InsuranceFilesDialog.vue';

export default {
  beforeRouteEnter(to, from, next) {
    checkRouteAccess('car_show_insurance', next);
  },
  components: {
    InsuranceColorSelect,
    EditOnClickField,
    MenuDatepicker,
    TextFieldEditDialog,
    InsuranceFilesDialog,
  },
  data: () => ({
    loading: false,
    searchString: '',
    filters: {
      search: '',
      expiryKaskoColor: '',
      expiryOsgpoColor: '',
      closeCheckupColor: '',
    },
    editDialog: {
      opened: false,
      param: '',
      label: '',
      value: '',
      itemId: null,
    },
    insuranceFilesDialog: {
      opened: false,
      date: '',
      files: [],
      type: '',
      itemId: '',
      onlyFiles: false,
      readOnly: false,
    },
    snack: {
      opened: false,
      color: '',
      message: '',
    },
  }),
  watch: {
    filters: {
      deep: true,
      immediate: true,
      handler() {
        this.fetchList();
      },
    },
  },
  computed: {
    ...mapState({
      list: ({ auto }) => auto.insuranceInspection.list,
      currentPage: ({ auto }) => auto.insuranceInspection.currentPage,
      lastPage: ({ auto }) => auto.insuranceInspection.lastPage,
      perPage: ({ auto }) => auto.insuranceInspection.perPage,
    }),
    headers() {
      return [
        { text: this.$t('common.number') },
        {
          text: this.$t('autosList.tableHeaders.id'),
        },
        { text: this.$t('autosList.tableHeaders.number') },
        {
          text: this.$t('autosList.tableHeaders.brand'),
        },
        {
          text: this.$t('autosList.tableHeaders.model'),
        },
        {
          text: this.$t('autosList.tableHeaders.year'),
        },
        {
          text: this.$t('auto.labels.kaskoName'),
        },
        {
          text: this.$t('auto.labels.expireDateKasko'),
        },
        {
          text: this.$t('auto.labels.osgpoName'),
        },
        {
          text: this.$t('auto.labels.expireDateOsgpo'),
        },
        {
          text: this.$t('auto.labels.checkup'),
          sections: [
            { text: this.$t('common.fields.openCheckup') },
            { text: this.$t('common.fields.closeCheckup') },
          ],
        },
        {
          hidden: true,
        },
      ];
    },
    totalColspan() {
      return this.headers.reduce((acc, header) => {
        let total = acc;

        if (header.sections) {
          total += header.sections.length;
        } else {
          total += 1;
        }

        return total;
      }, 0);
    },
  },
  methods: {
    async fetchList(page = 1) {
      this.loading = true;

      await this.$store.dispatch('auto/fetchInsuranceInspectionList', {
        ...this.filters,
        page,
      });

      this.loading = false;
    },
    updateInsurance(params) {
      let data = {};

      if (params.expiryKasko || params.expiryOsgpo) {
        data = {
          [this.insuranceFilesDialog.type]: '1',
          ...params,
        };
      } else {
        data = {
          [this.insuranceFilesDialog.type]: '0',
          ...params,
        };
      }

      this.updateItem(this.insuranceFilesDialog.itemId, data);
    },
    // updateKasko(params) {
    //   const params = {
    //     kasko: 1,
    //     expiryKasko: date,
    //   };

    //   if (!date) {
    //     params.kasko = 0;
    //     params.expiryKasko = date;
    //     params.kaskoName = '';
    //   }

    //   this.updateItem(id, params);
    // },
    // updateOsgpo(id, date) {
    //   const params = {
    //     osgpo: 1,
    //     expiryOsgpo: date,
    //   };

    //   if (!date) {
    //     params.osgpo = 0;
    //     params.expiryOsgpo = date;
    //     params.osgpoName = '';
    //   }

    //   this.updateItem(id, params);
    // },
    async updateItem(id, params) {
      this.loading = true;
      const { error } = await this.$store.dispatch('auto/updateInsuranceInfo', {
        id,
        params,
      });

      const snack = {
        opened: true,
        color: 'success',
        message: this.$t('common.saved'),
      };

      if (error) {
        snack.color = 'error';
        snack.message = this.$t('common.errors.APIerror');
      } else {
        await this.fetchList(this.currentPage);
      }

      this.snack = snack;
      this.loading = false;
    },
    getExpireStateColor(days) {
      if (days > 60) return 'green lighten-4';
      if (days >= 31) return 'yellow lighten-4';

      return 'red lighten-4';
    },
    async onInsuranceFilesDelete() {
      await this.fetchList(this.currentPage);
      const auto = this.list.find(
        (item) => item.id === this.insuranceFilesDialog.itemId,
      );
      this.insuranceFilesDialog.files = // eslint-disable-line
        auto[`${this.insuranceFilesDialog.type}Files`];
    },
  },
};
</script>

<style lang="scss">
.insurance {
  &__search {
    max-width: 400px;
    margin-bottom: 10px !important;
  }

  &__filter {
    max-width: 250px;
    margin-right: 30px;
  }

  &__list {
    .v-data-table-header {
      tr:first-child th {
        height: 30px !important;
        font-weight: 600 !important;
        text-align: center !important;

        &.section {
          border-right: 1px dashed rgba(255, 255, 255, 0.5);
          border-bottom: 1px dashed rgba(255, 255, 255, 0.5);
        }
      }

      th {
        height: 35px !important;

        &.section {
          background-color: lighten(#424242, 10%) !important;

          &.last {
            border-right: 1px dashed rgba(255, 255, 255, 0.5);
          }
        }
      }
    }
  }

  .date-input {
    font-size: 13px;
    margin-bottom: 5px;
  }

  .edit-btn {
    font-weight: 600;
    color: #464646;

    &:not([disabled]):hover {
      opacity: 0.7;
    }

    &[disabled] {
      font-weight: normal;
      color: inherit;
    }
  }
}

.v-pagination {
  button {
    box-shadow: none !important;
  }
}

.nowrap {
  white-space: nowrap;
}
</style>
